import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { payoutsApi } from '../../services/payoutsApi';
import { PayoutDetailResponse, PayoutLineResponse, PayoutReconciliationResponse, PayoutStatus, PayoutSummaryResponse } from '../../types/payouts';
import Money from './Money';
import InfoTip from './InfoTip';

const ARRIVAL = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** `YYYY-MM-DD` (Stripe arrival_date) → "18 Sep 2026"; unparseable input is shown as-is. */
export function formatArrivalDate(date: string | null | undefined): string {
  if (!date) return '—';
  const d = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? date : ARRIVAL.format(d);
}

/** Stripe payout status → the admin badge palette (approved / pending / rejected / update-requested). */
export function PayoutStatusBadge({ status }: { status: PayoutStatus | string }) {
  const s = String(status).toUpperCase();
  const cls = s === 'PAID' ? 'approved'
    : s === 'FAILED' || s === 'CANCELED' ? 'rejected'
    : s === 'IN_TRANSIT' ? 'update-requested'
    : 'pending';
  return <span className={`admin-badge ${cls}`} data-testid="payout-status">{s.replace(/_/g, ' ')}</span>;
}

/**
 * #563 — which of the three reconciliation states a payout is in. The drawer header used to print
 * `txnCount ?? '—'`, so "not reconciled yet", "no breakdown exists" and "zero transactions" all
 * read as the same failed load.
 *
 * <ul>
 *   <li>{@code counted} — billing linked the balance transactions and the numbers are real. Zero is
 *       a real answer and is shown as "0 transactions", not as a dash.</li>
 *   <li>{@code notApplicable} — a manual payout: Stripe exposes no balance transactions for one, so
 *       there is nothing to count and never will be.</li>
 *   <li>{@code pending} — an automatic payout whose link has not run yet; the next sync fills it.</li>
 * </ul>
 */
export type ReconciliationState =
  | { kind: 'counted'; txnCount: number; matchedCount: number }
  | { kind: 'notApplicable' }
  | { kind: 'pending' };

type CountFields = Pick<PayoutSummaryResponse, 'automatic' | 'txnCount' | 'matchedCount'>;

export function reconciliationState(
  summary: CountFields,
  recon: Pick<PayoutReconciliationResponse, 'available'> | null,
): ReconciliationState {
  if (recon?.available !== false && summary.txnCount != null && summary.matchedCount != null) {
    return { kind: 'counted', txnCount: summary.txnCount, matchedCount: summary.matchedCount };
  }
  // `automatic === false` is billing's own test for REASON_MANUAL, so the two can never disagree.
  return summary.automatic === false ? { kind: 'notApplicable' } : { kind: 'pending' };
}

/** The header copy for that state — words, never a bare em dash. */
export function reconciliationCountsLabel(state: ReconciliationState): string {
  switch (state.kind) {
    case 'counted':
      return `${state.txnCount} transaction${state.txnCount === 1 ? '' : 's'} · ${state.matchedCount} matched`;
    case 'notApplicable':
      return 'no transaction breakdown — manual payout';
    case 'pending':
      return 'transactions not linked yet';
  }
}

/** Stripe's lower-case balance transaction type (`charge`, `stripe_fee`, …) → "CHARGE", "STRIPE FEE". */
function formatLineType(type: string): string {
  return String(type).toUpperCase().replace(/_/g, ' ');
}

/** "Matched to" cell: the record kind + order reference, or the AC-29b exception badge. */
function MatchedTo({ line }: { line: PayoutLineResponse }) {
  if (!line.matchedTo) {
    return <span className="admin-badge rejected" data-testid="unmatched">Unmatched</span>;
  }
  const { kind, orderRef, id } = line.matchedTo;
  return (
    <span>
      <span style={{ textTransform: 'capitalize' }}>{String(kind).toLowerCase()}</span>
      {' · '}
      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} title={id}>{orderRef ?? `${id.slice(0, 8)}…`}</span>
    </span>
  );
}

function SummaryCell({ label, children, tone }: { label: string; children: ReactNode; tone?: 'danger' | 'success' }) {
  return (
    <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>{label}</div>
      <div style={{
        fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem',
        color: tone === 'danger' ? 'var(--admin-danger)' : tone === 'success' ? 'var(--admin-success)' : 'var(--admin-text)',
      }}>
        {children}
      </div>
    </div>
  );
}

interface PayoutDetailProps {
  /** The list row that was clicked (its `payoutId` UUID is the route key); `null` keeps the drawer closed. */
  payout: PayoutSummaryResponse | null;
  onClose: () => void;
}

/**
 * Spec 053 AC-29b — payout reconciliation drawer. Same right-hand panel treatment as
 * the other admin drawers. Shows the payout against the balance transactions Stripe lists for it:
 * actual (Stripe amount) vs expected (Σ matched line net) vs variance, then every line with what
 * it matched. A line with no match is the AC-29b exception, shown as a red "Unmatched" badge.
 * When billing reports `reconciliation.available === false` (#540: manual payout, or link still
 * pending) only the payout amount is shown plus a neutral notice with `unavailableReason` — no
 * expected/variance cells and no empty lines table, since the zeros are placeholders, not a finding.
 * Reads billing's `PayoutDetailResponse` as-is: `payout` (the summary), `reconciliation`, `lines`.
 */
export default function PayoutDetail({ payout, onClose }: PayoutDetailProps) {
  const auth = useAuth();
  const payoutId = payout?.payoutId ?? null;
  const [detail, setDetail] = useState<PayoutDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!payoutId || !auth.token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    payoutsApi.get(payoutId, auth.token)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the payout'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [payoutId, auth.token]);

  useEffect(() => {
    if (!payoutId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [payoutId, onClose]);

  if (!payout) return null;

  // The list row is enough for the header; the fetched detail supersedes it once it lands.
  const summary = detail?.payout ?? payout;
  const recon = detail?.reconciliation ?? null;
  // #540: manual payouts and not-yet-linked ones have no line breakdown — billing sends
  // available=false with zero placeholders, which must not be read as a variance.
  const unavailable = recon ? recon.available === false : false;
  const exceptionCount = recon ? recon.exceptions.length : 0;
  const reconciled = recon ? Math.abs(recon.variance) < 0.005 && exceptionCount === 0 : false;

  return (
    <div className="admin-modal-overlay" style={{ justifyContent: 'flex-end', alignItems: 'stretch' }} onClick={onClose}>
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Payout ${summary.gatewayPayoutId}`}
        style={{
          width: '720px', maxWidth: '100vw', height: '100vh', margin: 0,
          borderRadius: '0.75rem 0 0 0.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Payout <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>{summary.gatewayPayoutId}</span>
            <PayoutStatusBadge status={summary.status} />
          </h3>
          <button type="button" className="admin-modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>

        <div className="admin-modal-body" style={{ flex: 1 }}>
          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
          {loading && <div className="admin-loading"><div className="admin-loading-spinner"></div></div>}

          {detail && recon && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginBottom: '0.75rem' }} data-testid="payout-meta">
                Arrives {formatArrivalDate(summary.arrivalDate)}
                {' · '}
                <span data-testid="payout-counts" data-state={reconciliationState(summary, recon).kind}>
                  {reconciliationCountsLabel(reconciliationState(summary, recon))}
                </span>
                {detail.destination && <> · to <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{detail.destination}</span></>}
                {summary.failureReason && (
                  <div className="admin-info-banner danger" role="status" style={{ marginTop: '0.5rem' }}>
                    <strong>Payout failed{detail.failureCode ? ` (${detail.failureCode})` : ''}:</strong> {summary.failureReason}
                  </div>
                )}
              </div>

              {unavailable ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }} data-testid="reconciliation">
                    <SummaryCell label="Payout amount (Stripe)">
                      <Money amount={recon.actual} glossaryKey="payoutAmount" />
                    </SummaryCell>
                  </div>
                  <div className="admin-info-banner info" role="status" data-testid="reconciliation-unavailable" style={{ marginTop: '0.75rem' }}>
                    <strong>Reconciliation not available.</strong>{' '}
                    {recon.unavailableReason ?? 'No balance transactions are available for this payout'}
                  </div>
                </>
              ) : (
                <>
                  {/* Reconciliation summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }} data-testid="reconciliation">
                    <SummaryCell label="Payout amount (Stripe)">
                      <Money amount={recon.actual} glossaryKey="payoutAmount" />
                    </SummaryCell>
                    <SummaryCell label="Expected from transactions">
                      <Money amount={recon.expected} glossaryKey="payoutExpected" />
                    </SummaryCell>
                    <SummaryCell label="Variance" tone={reconciled ? 'success' : 'danger'}>
                      <Money amount={recon.variance} glossaryKey="payoutVariance" />
                    </SummaryCell>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                    {reconciled
                      ? <span className="admin-badge approved">Reconciled</span>
                      : <span className="admin-badge rejected" data-testid="exceptions-count">{exceptionCount} exception{exceptionCount === 1 ? '' : 's'}</span>}
                  </div>

                  {/* Lines */}
                  <div className="admin-table-wrapper" style={{ marginTop: '1rem' }}>
                    <table className="admin-table" data-testid="payout-lines">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Stripe ref</th>
                          <th style={{ textAlign: 'right' }}>Gross<InfoTip glossaryKey="payoutLineGross" /></th>
                          <th style={{ textAlign: 'right' }}>Fee<InfoTip glossaryKey="payoutLineFee" /></th>
                          <th style={{ textAlign: 'right' }}>Net<InfoTip glossaryKey="payoutLineNet" /></th>
                          <th>Matched to</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.lines.length === 0 ? (
                          <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No balance transactions on this payout</td></tr>
                        ) : detail.lines.map((line) => (
                          <tr key={line.balanceTransactionId} data-testid="payout-line" data-unmatched={line.matchedTo ? undefined : 'true'}>
                            <td>{formatLineType(line.type)}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} title={line.sourceRef ?? undefined}>{line.balanceTransactionId}</td>
                            <td style={{ textAlign: 'right' }}><Money amount={line.gross} glossaryKey="payoutLineGross" hideTip /></td>
                            <td style={{ textAlign: 'right' }}><Money amount={line.fee} glossaryKey="payoutLineFee" hideTip /></td>
                            <td style={{ textAlign: 'right' }}><Money amount={line.net} glossaryKey="payoutLineNet" hideTip /></td>
                            <td><MatchedTo line={line} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
